import { DateRange } from '@/modules/core/daterange/models/DateRange';
import DateRangeResource from '@/modules/core/daterange/resources/DateRangeResource';
import { getters } from '@/modules/core/app/helpers/store';
import {
  DATE_RANGE_QUERY_PARAM,
  MomentDateFormat,
} from '@/modules/core/daterange/daterange.constants';
import { DateUtil } from '@/modules/core/app/utils/DateUtil';
import { DashboardEventType } from '@/modules/ta/dashboard/dashboard.constants';
import EventBus from '@/modules/core/app/helpers/EventBus';
import { PublicShareService } from '@/modules/core/app/services/PublicShareService';

export class DateRangeService {
  /**
   * @returns {Promise<*>}
   */
  async getDateRange() {
    return DateRangeResource.getData(DateRange);
  }

  /**
   * @param {DateRange} dateRange
   * @returns {Promise<*>}
   */
  async setDateRange(dateRange) {
    // A date-scoped share re-renders server side, so signalling the widgets first
    // would only start fetches the navigation throws away (TR-7637).
    if (this.reloadPublicShareForDateRange(dateRange)) {
      return dateRange;
    }

    EventBus.signal(DashboardEventType.EDIT_DATE_RANGE, dateRange);

    // Public viewer has no app session to persist to (endpoint would 401).
    if (PublicShareService.isPublicShareView()) {
      return dateRange;
    }

    return DateRangeResource.postData(dateRange);
  }

  /**
   * Section and widget visibility for product assignments is resolved when the
   * server renders the share, so on those dashboards the new range is handed
   * back to the server; everywhere else the widgets just re-fetch (TR-7637).
   *
   * @param {DateRange} dateRange
   * @returns {boolean} whether the viewer is navigating
   */
  reloadPublicShareForDateRange(dateRange) {
    if (!PublicShareService.isPublicShareView() || !PublicShareService.hasDateScopedVisibility()) {
      return false;
    }

    const url = new URL(window.location.href);
    const dateQuery = this.buildDateQueryParam(dateRange);
    if (url.searchParams.get(DATE_RANGE_QUERY_PARAM) === dateQuery) {
      return false;
    }

    url.searchParams.set(DATE_RANGE_QUERY_PARAM, dateQuery);
    EventBus.signal(DashboardEventType.PUBLIC_SHARE_RELOADING);
    // Replace so the back button does not walk through every range tried.
    window.location.replace(url.toString());

    return true;
  }

  /**
   * @returns {Promise<*>}
   */
  async getRelativeDateRanges() {
    return DateRangeResource.getRelativeDateRanges();
  }

  /**
   * Builds a query string for datarange query param in ISO (ex: ?daterange=2014-01-01|2015-12-12)
   * @param {DateRange} dateRange
   * @returns {string}
   */
  buildDateQueryParam(dateRange = null) {
    dateRange = dateRange || getters.daterange.getDateRange();

    const startDate = this._formatDateRange(dateRange.start_date);
    const endDate = this._formatDateRange(dateRange.end_date);

    let dateQuery = `${startDate}|${endDate}`;

    if (dateRange.is_comparison_active) {
      const comparisonStartDate = this._formatDateRange(dateRange.comparison_start_date);
      const comparisonEndDate = this._formatDateRange(dateRange.comparison_end_date);
      dateQuery += `,${comparisonStartDate}|${comparisonEndDate}`;
    }

    return dateQuery;
  }

  /**
   * @param value
   * @returns {string}
   * @private
   */
  _formatDateRange(value) {
    return DateUtil.formatDate(value, MomentDateFormat.ISO);
  }
}

export default new DateRangeService();
